// RB-MY01-PalmGarden.web/src/components/PlantManagement.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../services/api';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Switch,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableSortLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  LocalFlorist as PlantIcon,
  PhotoLibrary as PhotoIcon
} from '@mui/icons-material';
import { GridContainer, GridItem } from '../components/CustomGrid';

interface Plant {
  id: number;
  name: string;
  type: string;
  description: string;
  careInstructions: string;
  wateringFrequency: number;
  lightRequirement: string;
  published: boolean;
  imageUrl: string;
  createdAt: string;
  updatedAt: string;
}

type Order = 'asc' | 'desc';

interface HeadCell {
  id: keyof Plant;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  { id: 'name', label: 'Plant Name', numeric: false },
  { id: 'type', label: 'Type', numeric: false },
  { id: 'published', label: 'Status', numeric: false },
  { id: 'createdAt', label: 'Created', numeric: false },
  { id: 'updatedAt', label: 'Updated', numeric: false },
];

const PlantManagement: React.FC = () => {
  const navigate = useNavigate();
  const api = useApi();
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState<keyof Plant>('updatedAt');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedPlants, setSelectedPlants] = useState<number[]>([]);
  const [bulkActionDialog, setBulkActionDialog] = useState(false);
  const [bulkAction, setBulkAction] = useState<'publish' | 'unpublish' | 'delete'>('publish');

  const fetchPlants = async () => {
    try {
      setLoading(true);
      const data = await api.getPlants();
      setPlants(data);
    } catch (err) {
      console.error('Error fetching plants:', err);
      setError('Failed to load plants');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlants();
  }, []);

  // Filter and sort plants
  const filteredPlants = plants
    .filter(plant => {
      const matchesSearch = plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          plant.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          plant.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || plant.type === filterType;
      const matchesStatus = filterStatus === 'all' || 
                           (filterStatus === 'published' && plant.published) ||
                           (filterStatus === 'unpublished' && !plant.published);
      
      return matchesSearch && matchesType && matchesStatus;
    })
    .sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (order === 'desc') {
        return (bValue as any) < (aValue as any) ? -1 : (bValue as any) > (aValue as any) ? 1 : 0;
      } else {
        return (aValue as any) < (bValue as any) ? -1 : (aValue as any) > (bValue as any) ? 1 : 0;
      }
    });

  const plantTypes = ['all', ...new Set(plants.map(plant => plant.type))];

  const handleRequestSort = (property: keyof Plant) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = filteredPlants.map(n => n.id);
      setSelectedPlants(newSelected);
      return;
    }
    setSelectedPlants([]);
  };

  const handleClick = (id: number) => {
    const selectedIndex = selectedPlants.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedPlants, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedPlants.slice(1));
    } else if (selectedIndex === selectedPlants.length - 1) {
      newSelected = newSelected.concat(selectedPlants.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedPlants.slice(0, selectedIndex),
        selectedPlants.slice(selectedIndex + 1),
      );
    }

    setSelectedPlants(newSelected);
  };

  const isSelected = (id: number) => selectedPlants.indexOf(id) !== -1;

  const handleBulkAction = async () => {
    try {
      for (const plantId of selectedPlants) {
        const plant = plants.find(p => p.id === plantId);
        if (!plant) continue;

        switch (bulkAction) {
          case 'publish':
            await api.updatePlant(plantId, { 
              ...plant, 
              published: true,
              careInstructions: plant.careInstructions || '',
              wateringFrequency: plant.wateringFrequency || 7,
              lightRequirement: plant.lightRequirement || 'Medium',
              imageUrl: plant.imageUrl || ''
            });
            break;
          case 'unpublish':
            await api.updatePlant(plantId, { 
              ...plant, 
              published: false,
              careInstructions: plant.careInstructions || '',
              wateringFrequency: plant.wateringFrequency || 7,
              lightRequirement: plant.lightRequirement || 'Medium',
              imageUrl: plant.imageUrl || ''
            });
            break;
          case 'delete':
            await api.deletePlant(plantId);
            break;
        }
      }
      
      setSelectedPlants([]);
      setBulkActionDialog(false);
      fetchPlants(); // Refresh the list
    } catch (err) {
      console.error('Error performing bulk action:', err);
      setError('Failed to perform bulk action');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
            🌿 Plant Management
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage your plant database - {plants.length} total plants
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPlants}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/plants/add')}
          >
            Add Plant
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters and Search */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <GridContainer spacing={2}>
            <GridItem xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search plants..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </GridItem>
            <GridItem xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Type</InputLabel>
                <Select
                  value={filterType}
                  label="Filter by Type"
                  onChange={(e) => setFilterType(e.target.value)}
                >
                  {plantTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type === 'all' ? 'All Types' : type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Filter by Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Filter by Status"
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="published">Published</MenuItem>
                  <MenuItem value="unpublished">Unpublished</MenuItem>
                </Select>
              </FormControl>
            </GridItem>
            <GridItem xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setBulkActionDialog(true)}
                disabled={selectedPlants.length === 0}
              >
                Bulk Actions ({selectedPlants.length})
              </Button>
            </GridItem>
          </GridContainer>
        </CardContent>
      </Card>

      {/* Results Summary */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredPlants.length} of {plants.length} plants
        </Typography>
        {selectedPlants.length > 0 && (
          <Typography variant="body2" color="primary">
            {selectedPlants.length} plant(s) selected
          </Typography>
        )}
      </Box>

      {/* Plants Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedPlants.length > 0 && selectedPlants.length < filteredPlants.length}
                  checked={filteredPlants.length > 0 && selectedPlants.length === filteredPlants.length}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              {headCells.map((headCell) => (
                <TableCell
                  key={headCell.id}
                  sortDirection={orderBy === headCell.id ? order : false}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : 'asc'}
                    onClick={() => handleRequestSort(headCell.id)}
                  >
                    {headCell.label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlants
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((plant) => {
                const isItemSelected = isSelected(plant.id);
                return (
                  <TableRow
                    hover
                    key={plant.id}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onClick={() => handleClick(plant.id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar src={plant.imageUrl} sx={{ width: 40, height: 40, mr: 2 }}>
                          <PlantIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            {plant.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {plant.description.substring(0, 50)}...
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={plant.type} size="small" />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plant.published ? 'Published' : 'Draft'}
                        color={plant.published ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{formatDate(plant.createdAt)}</TableCell>
                    <TableCell>{formatDate(plant.updatedAt)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View">
                          <IconButton size="small" onClick={() => navigate(`/admin/plants/${plant.id}`)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => navigate(`/admin/plants/edit/${plant.id}`)}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={plant.published ? 'Unpublish' : 'Publish'}>
                          <IconButton 
                            size="small" 
                            onClick={async () => {
                              try {
                                await api.updatePlant(plant.id, { 
                                  ...plant, 
                                  published: !plant.published,
                                  careInstructions: plant.careInstructions || '',
                                  wateringFrequency: plant.wateringFrequency || 7,
                                  lightRequirement: plant.lightRequirement || 'Medium',
                                  imageUrl: plant.imageUrl || ''
                                });
                                fetchPlants();
                              } catch (err) {
                                setError('Failed to update plant status');
                              }
                            }}
                          >
                            {plant.published ? <UnpublishIcon /> : <PublishIcon />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this plant?')) {
                                try {
                                  await api.deletePlant(plant.id);
                                  fetchPlants();
                                } catch (err) {
                                  setError('Failed to delete plant');
                                }
                              }
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredPlants.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialog} onClose={() => setBulkActionDialog(false)}>
        <DialogTitle>Bulk Actions</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Perform action on {selectedPlants.length} selected plant(s):
          </Typography>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={bulkAction}
              label="Action"
              onChange={(e) => setBulkAction(e.target.value as 'publish' | 'unpublish' | 'delete')}
            >
              <MenuItem value="publish">Publish</MenuItem>
              <MenuItem value="unpublish">Unpublish</MenuItem>
              <MenuItem value="delete">Delete</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialog(false)}>Cancel</Button>
          <Button onClick={handleBulkAction} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlantManagement; 